import {
  AudienceInstance,
  ConditionalObjectType,
  Group,
  Segmentation,
  WorkflowType,
} from '@/client/services/api/graphql/gql/graphql';
import { Divider, HStack, Stack } from '@chakra-ui/react';
import { useEffect, useState } from 'react';

import type { AssignmentFormData } from '../../../../types/assignmentTypes';
import AssignmentSegmentationTag from '../../../lists/list-items/AssignmentSegmentationTag';
import AudienceSearch from '../../../../input/AudienceSearch';
import WarmNeutralSelectionMenu from '../WarmNeutralSelectionMenu';
import { useAuthStore } from '@/client/services/state/authStore';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export default function AddedUpdatedForm() {
  const { t } = useTranslation();
  const { authConfig } = useAuthStore();
  const { company } = authConfig;
  const { watch, setValue } = useFormContext<AssignmentFormData>();
  const { settings } = watch();
  const { type, conditionals } = settings;
  const [selectedSegmentationItems, setSelectedSegmentationItems] = useState<any>([]);

  const segmentationTypeToConditionalTypeMap = {
    location: ConditionalObjectType.LocationAdded,
    department: ConditionalObjectType.DepartmentAdded,
    group: ConditionalObjectType.GroupAdded,
    team: ConditionalObjectType.TeamAdded,
    grade: ConditionalObjectType.GradeAdded,
    org_level: ConditionalObjectType.OrgLevelAdded,
  };

  const conditionalTypeToSegmentationTypeMap = {
    [ConditionalObjectType.LocationAdded]: 'location',
    [ConditionalObjectType.DepartmentAdded]: 'department',
    [ConditionalObjectType.GroupAdded]: 'group',
    [ConditionalObjectType.TeamAdded]: 'team',
    [ConditionalObjectType.GradeAdded]: 'grade',
    [ConditionalObjectType.OrgLevelAdded]: 'org_level',
  };

  // * Set conditionals when component is rendered for workflow edit
  useEffect(() => {
    if (conditionals) {
      setSelectedSegmentationItems(
        conditionals.map((conditional) => ({
          ...conditional,
          type: 'segment',
          segmentationType:
            conditionalTypeToSegmentationTypeMap[
              conditional.type as keyof typeof conditionalTypeToSegmentationTypeMap
            ],
        })),
      );
    }
  }, []);

  // * Update conditionals when selectedSegmentationItems changes
  useEffect(() => {
    // Check if any items were removed
    const hasRemovedItems = conditionals?.some(
      (conditional) =>
        !selectedSegmentationItems.some((item: Segmentation & Group) => item.id === conditional.id),
    );

    // Check if any new items were added
    const hasNewItems = selectedSegmentationItems.some(
      (item: Segmentation & Group) =>
        !conditionals?.some((conditional) => conditional.id === item.id),
    );

    // Update conditionals if there are changes
    if (hasNewItems || hasRemovedItems) {
      setValue(
        'settings.conditionals',
        selectedSegmentationItems.map((item: Segmentation & Group) => ({
          id: item.id,
          name: item.name,
          type: segmentationTypeToConditionalTypeMap[
            item.segmentationType as keyof typeof segmentationTypeToConditionalTypeMap
          ],
        })),
      );
    }
  }, [selectedSegmentationItems]);

  const userOptions = [
    {
      label: `${t('admin.assignments.automations.addedUpdated.addedTo')} ${company.name}`,
      value: WorkflowType.UserAdded,
      onClick: () => {
        setValue('settings.conditionals', []);
        setSelectedSegmentationItems([]);
        setValue('settings.type', WorkflowType.UserAdded);
      },
    },
    {
      label: t('admin.assignments.automations.addedUpdated.updated'),
      value: WorkflowType.UserUpdated,
      onClick: () => setValue('settings.type', WorkflowType.UserUpdated),
    },
    {
      label: t('admin.assignments.automations.addedUpdated.addedOrUpdated'),
      value: WorkflowType.UserAddedOrUpdated,
      onClick: () => setValue('settings.type', WorkflowType.UserAddedOrUpdated),
    },
  ];

  const getLabelFromUserType = (userType: WorkflowType) =>
    userOptions.find((option) => option.value === userType)?.label ||
    t('admin.assignments.automations.userAddedUpdated.selectUserType');

  const renderSegmentationSelection =
    type === WorkflowType.UserAddedOrUpdated || type === WorkflowType.UserUpdated;

  const handleSelectSegmentation = (selectedSegmentationItem: Segmentation & Group) => {
    if (
      selectedSegmentationItems.some(
        (item: Segmentation & Group) => item.id === selectedSegmentationItem.id,
      )
    ) {
      setSelectedSegmentationItems(
        selectedSegmentationItems.filter(
          (item: Segmentation & Group) => item.id !== selectedSegmentationItem.id,
        ),
      );
    } else {
      setSelectedSegmentationItems([
        ...selectedSegmentationItems,
        { ...selectedSegmentationItem, type: 'segment' },
      ]);
    }
  };

  const handleRemoveSegmentation = (removalItem: AudienceInstance) => {
    setSelectedSegmentationItems(
      selectedSegmentationItems.filter((item: Segmentation & Group) => item.id !== removalItem.id),
    );
  };

  return (
    <Stack spacing={8}>
      <WarmNeutralSelectionMenu
        label={t('admin.assignments.automations.userAddedUpdated.selectUserType')}
        buttonLabel={getLabelFromUserType(type)}
        options={userOptions}
      />

      {renderSegmentationSelection && (
        <>
          <Divider />

          <Stack>
            <AudienceSearch
              label={t('admin.assignments.automations.userAddedUpdated.selectSegmentation')}
              helpText={t(
                'admin.assignments.automations.userAddedUpdated.selectSegmentation.helpText',
              )}
              handleSelectItem={handleSelectSegmentation}
              selectedItems={selectedSegmentationItems}
              hasError={false}
            />

            {selectedSegmentationItems.length > 0 && (
              <HStack flexWrap="wrap" gap={0}>
                {selectedSegmentationItems.map((item: AudienceInstance) => (
                  <AssignmentSegmentationTag
                    key={item.id}
                    item={item}
                    handleRemoveItem={handleRemoveSegmentation}
                  />
                ))}
              </HStack>
            )}
          </Stack>
        </>
      )}
    </Stack>
  );
}
